import React, { useState } from "react"
import {
  Box,
  Checkbox,
  ClickAwayListener,
  InternalIcon,
  Link,
  Stack,
  Tile,
  Tooltip,
  Typography,
  useTheme
} from "@carrotfertility/carotene-core"
import { CarrotPlan, CarrotPlanStep } from "#/types/carrotPlanTypes"
import useCarrotPlanMenuItems, { CarrotPlanStepMenuItem } from "../../carrot-plans/hooks/useCarrotPlanMenuItems"
import { reportErrorMessage } from "#/utils/ErrorReporting"
import useUpdatePlanStep from "../../carrot-plans/hooks/useUpdatePlanStep"
import { FormattedMessage, useIntl } from "react-intl"
import moment from "moment"
import { useQuery } from "@tanstack/react-query"
import { getCarrotPlanQueryKey } from "#/components/carrot-plans/hooks/useCarrotPlan"
import { ErrorPage } from "#/components/views/app/ErrorPage"
import { useMemberActionTracking } from "#/services/memberActionTracking/useMemberActionTracking"
import { MemberActions } from "#/services/memberActionTracking/memberActions"
import { CarrotCompanionCarrotPlanMessage } from "#/components/care-team/CarrotCompanionCarrotPlanMessage"
import { InAppMessagingContextProvider } from "#/components/context/messaging/InAppMessagingContext"
import { useCarrotCompanionCarrotPlanMessage } from "#/components/care-team/hooks/useCarrotCompanion"
import { useMessageDisplayState } from "#/components/care-team/utils/carrotCompanionMessageUtil"
import { CarrotCompanionTile } from "#/components/care-team/CarrotCompanionTile"
import { useUserRole } from "#/components/partner-access/hooks/usePartnerAccess"
import { updateCarrotPlanMessageDismissTimestamp } from "#/components/views/modal/utils/localStorageUtils"
import { CarrotPlanFooter } from "#/components/home/carrot-plan/CarrotPlanFooter"

type CarrotPlanHeroProps = {
  plan: CarrotPlan
}

type PlanStepProps = {
  carrotPlanStepMenuItem: CarrotPlanStepMenuItem
}

interface StepContentProps extends Omit<PlanStepProps, "employeeId">, React.HTMLAttributes<HTMLSpanElement> {
  stepCompleted: boolean
}

type StepCheckmarkProps = {
  carrotPlanStepMenuItem: CarrotPlanStepMenuItem
  stepCompleted: boolean
  handleStepCompleted: (stepId: number) => void
}

const ReadAndLearnMenuItems = {
  Article: "6P6c1JhtDMcVIk9DE8u1Ca",
  Video: "6cjqRZ2djmEpFH1eD0elbX",
  Group: "1D4fLW2eWfCnRGd3v0z2Wx",
  Guide: "20nMPmMswstXjq3xdBXEzi"
}

const ReadAndLearnMenuItemIds = Object.values(ReadAndLearnMenuItems)

export default function CarrotPlanHero({ plan }: CarrotPlanHeroProps): JSX.Element {
  const intl = useIntl()
  const { locale } = intl
  const {
    data: carrotPlanStepMenuItems,
    isLoading: menuItemsLoading,
    error: menuItemsError
  } = useCarrotPlanMenuItems(plan.steps, locale)

  const { data: cpMessage } = useCarrotCompanionCarrotPlanMessage()
  const { isPartnerAccessingAccount } = useUserRole()
  const shouldShowMessage = useMessageDisplayState(cpMessage?.carrotPlanMessage)
  const [hasUserDismissed, setHasUserDismissed] = useState(false)
  const theme = useTheme()

  const isMessageDismissed = !shouldShowMessage || hasUserDismissed

  const handleDismiss = () => {
    setHasUserDismissed(true)
    updateCarrotPlanMessageDismissTimestamp()
  }

  if (menuItemsError) {
    reportErrorMessage(
      `Unable to retrieve Carrot Plan menu items from Contentful for employee ${plan.employeeId}, error: ${menuItemsError}`
    )
    return <ErrorPage />
  }

  return (
    <>
      {!menuItemsLoading ? (
        <Tile
          id="carrot-plans-hero"
          component="article"
          sx={{
            padding: (theme) => theme.spacing(theme.tokens.spacing.xl)
          }}
          variant="primary"
        >
          <Stack spacing={theme.spacing(theme.tokens.spacing.xl)}>
            <CarrotPlanHeader createdAtUtcDate={moment(plan.createdAtUtc).toDate()} />
            <InAppMessagingContextProvider>
              {!isMessageDismissed && !isPartnerAccessingAccount ? (
                <CarrotCompanionCarrotPlanMessage onDismiss={handleDismiss} />
              ) : (
                <CarrotCompanionTile />
              )}
            </InAppMessagingContextProvider>
            <CarrotPlanDisplay carrotPlanStepMenuItems={carrotPlanStepMenuItems} />
            <CarrotPlanFooter plan={plan} />
          </Stack>
        </Tile>
      ) : null}
    </>
  )
}

type CarrotPlanHeaderProps = {
  createdAtUtcDate: Date
}

function CarrotPlanHeader({ createdAtUtcDate }: CarrotPlanHeaderProps): JSX.Element {
  const intl = useIntl()

  const formattedDate = intl.formatDate(createdAtUtcDate, {
    year: "numeric",
    month: "long",
    day: "numeric"
  })

  return (
    <Box
      alignItems="center"
      display="flex"
      flexDirection="row"
      justifyContent="space-between"
      marginBottom={(theme) => theme.tokens.spacing.xs}
    >
      <Box display="flex" gap={(theme) => theme.spacing(theme.tokens.spacing.xxs)} alignItems="center">
        <Typography variant="h3" color="inherit">
          <FormattedMessage defaultMessage="Your Carrot Plan" />
        </Typography>
      </Box>
      <Typography variant="body2" color="inherit" data-testid="carrot-plans-hero">
        <FormattedMessage defaultMessage="Updated {formattedDate}" values={{ formattedDate }} />
      </Typography>
    </Box>
  )
}

type CarrotPlanDisplayProps = {
  carrotPlanStepMenuItems: CarrotPlanStepMenuItem[]
}

function CarrotPlanDisplay({ carrotPlanStepMenuItems }: CarrotPlanDisplayProps): JSX.Element {
  return (
    <Box
      component="ul"
      display="flex"
      gap={(theme) => theme.spacing(theme.tokens.spacing.xxs)}
      flexDirection="column"
      sx={{ "li::before": { display: "none" } }}
    >
      {carrotPlanStepMenuItems.map((stepMenuItem) => (
        <Box
          padding={(theme) => theme.spacing(theme.tokens.spacing.md)}
          component="li"
          display="flex"
          gap={(theme) => theme.spacing(theme.tokens.spacing.lg)}
          flexDirection="column"
          key={stepMenuItem?.carrotPlanStepId}
          id={`cp-menuitem-${stepMenuItem?.menuItemId}`}
          borderRadius={(theme) => theme.tokens.borderRadius.sm}
        >
          <PlanStep carrotPlanStepMenuItem={stepMenuItem} />
        </Box>
      ))}
    </Box>
  )
}

export function getStepMenuItemDisplayText(carrotPlanStepMenuItem: CarrotPlanStepMenuItem): JSX.Element | string {
  const displayText = carrotPlanStepMenuItem?.displayText

  if (carrotPlanStepMenuItem?.readLearnArticleText) {
    const readLearnArticleText = carrotPlanStepMenuItem?.readLearnArticleText
    if (readLearnArticleText) {
      return displayText?.replace(/\[(.+?)]/g, readLearnArticleText)
    }
  } else if (
    ReadAndLearnMenuItemIds.includes(carrotPlanStepMenuItem.menuItemId) &&
    !carrotPlanStepMenuItem?.readLearnArticleText
  ) {
    return (
      <FormattedMessage defaultMessage="Check out our Carrot Academy resources for more information about your journey" />
    )
  }
  return displayText
}

function StepContent({ carrotPlanStepMenuItem: step, stepCompleted, ...otherProps }: StepContentProps): JSX.Element {
  const { mutate: updateMemberAction } = useMemberActionTracking()

  const showLink = React.useMemo(
    () => Boolean(step?.url) && !(step?.isLocked && stepCompleted),
    [step?.isLocked, step?.url, stepCompleted]
  )

  const handleLinkClick = (e: React.MouseEvent) => {
    e.preventDefault()

    const url = step?.url
    if (!url) return

    updateMemberAction(
      {
        memberActionTaken: MemberActions.CP_MENU_ITEM_CLICKED,
        metaData: {
          menuItemId: step.menuItemId,
          url
        }
      },
      {
        onSettled: () => {
          window.location.href = url
        }
      }
    )
  }

  if (showLink) {
    return (
      <Link
        color="inherit"
        underline="hover"
        sx={{
          display: "flex",
          alignItems: "center",
          gap: (theme) => theme.spacing(theme.tokens.spacing.xxxs),
          "&:hover": {
            textDecorationThickness: "2px",
            textDecorationLine: "underline",
            textDecorationColor: "inherit"
          }
        }}
        href={step?.url}
        id={`cp-item-${step?.carrotPlanStepId}-label`}
        onClick={handleLinkClick}
      >
        {getStepMenuItemDisplayText(step)}
        <InternalIcon color="primary" fontSize="small" />
      </Link>
    )
  }
  return (
    <Typography id={`cp-item-${step?.carrotPlanStepId}-label`} color="inherit" {...otherProps}>
      {step?.displayText}
    </Typography>
  )
}

function StepCheckmark({
  carrotPlanStepMenuItem: step,
  stepCompleted,
  handleStepCompleted
}: StepCheckmarkProps): JSX.Element {
  const [open, setOpen] = React.useState(false)
  const handleTooltipClose = () => {
    setOpen(false)
  }
  const handleTooltipOpen = () => {
    setOpen(!open)
  }
  return step?.isLocked ? (
    <ClickAwayListener onClickAway={handleTooltipClose}>
      <Tooltip
        onClose={handleTooltipClose}
        open={open}
        title={
          stepCompleted ? (
            <FormattedMessage defaultMessage="We checked this one for you" />
          ) : (
            <FormattedMessage defaultMessage="We'll check this one for you when it's done" />
          )
        }
      >
        <Checkbox
          readOnly
          checked={stepCompleted}
          onClick={handleTooltipOpen}
          inputProps={{ "aria-labelledby": `cp-item-${step?.carrotPlanStepId}-label` }}
          color="primary"
        />
      </Tooltip>
    </ClickAwayListener>
  ) : (
    <Checkbox
      checked={stepCompleted}
      onChange={() => handleStepCompleted(step?.carrotPlanStepId)}
      inputProps={{ "aria-labelledby": `cp-item-${step?.carrotPlanStepId}-label` }}
      color="primary"
    />
  )
}

function PlanStep({ carrotPlanStepMenuItem: step }: PlanStepProps): JSX.Element {
  const updatePlanStep = useUpdatePlanStep()

  // We use useQuery to subscribe to React Query's cache updates,
  // ensuring the UI automatically reflects optimistic updates
  // and server responses. This pattern maintains consistency
  // between the cache and component state.
  const carrotPlanQuery = useQuery<CarrotPlan>({ queryKey: getCarrotPlanQueryKey() })
  const currentStep =
    carrotPlanQuery.data?.steps.find((s: CarrotPlanStep) => s.carrotPlanStepId === step.carrotPlanStepId) ?? step
  const stepCompleted = currentStep?.completedAtUtc

  const handleStepCompleted = React.useCallback(() => {
    updatePlanStep.mutate({ stepId: step.carrotPlanStepId, completed: !stepCompleted })
  }, [updatePlanStep, stepCompleted, step.carrotPlanStepId])

  return (
    <Box display="flex" flexDirection="row" gap={(theme) => theme.spacing(theme.tokens.spacing.md)} alignItems="center">
      <StepCheckmark
        carrotPlanStepMenuItem={step}
        stepCompleted={!!stepCompleted}
        handleStepCompleted={handleStepCompleted}
      />
      <StepContent carrotPlanStepMenuItem={step} stepCompleted={!!step?.completedAtUtc} />
    </Box>
  )
}
